import React, { useState } from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { IReduxState } from 'interfaces/IReduxState'
import { useDispatch, useSelector } from 'react-redux'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import MuiAlert from '@mui/material/Alert'
import PackagingFinder from './packagingFinder'
import { IPackagingOrder } from 'interfaces/IPackaging'
import {
  IPackagingRequest,
  PackagingRequest,
  PackagingRequestSourceOrDestination,
} from 'interfaces/IPackagingRequest'
import API from '@aws-amplify/api'
import { IBatch } from 'interfaces/IPremix'

export default function PackagingRequestForm(props: {}) {
  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const [packagingRequest, setPackagingRequest] =
    React.useState<IPackagingRequest>(new PackagingRequest({}))
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasValidationErrors, setHasValidationErrors] = React.useState(false)
  const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null)
  // Use a key to force remount of child finders after save to clear internal state
  const [packagingFinderKey, setPackagingFinderKey] = useState<number>(0)

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }
  const getMissingFields = (req: IPackagingRequest): string[] => {
    const missing: string[] = []
    if (!req.inOutDirection) missing.push('In/Out')
    if (!req.sourceOrDestination) missing.push('Dari/Tujuan')

    const hasAny = Array.isArray(req.packagings)
      ? req.packagings.length > 0
      : false
    const hasQty = Array.isArray(req.packagings)
      ? req.packagings.some((p: any) => Number(p?.qty || 0) > 0)
      : false
    if (!hasAny || !hasQty) missing.push('Kemasan (dengan Qty)')

    // Notes required for some flows per labels
    const requiresNotesKeys = [
      PackagingRequestSourceOrDestination.return,
      PackagingRequestSourceOrDestination.inNoCost,
      PackagingRequestSourceOrDestination.outNoCost,
      PackagingRequestSourceOrDestination.fillingAdditional,
    ] as any
    if (
      (requiresNotesKeys.includes((req as any).sourceOrDestination) ||
        (req.sourceOrDestination ===
          PackagingRequestSourceOrDestination.stjTukarKemasan &&
          req.inOutDirection === VALUE_OUT)) &&
      !req.notes
    ) {
      missing.push('Notes')
    }

    return missing
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    const missing = getMissingFields(packagingRequest)
    if (missing.length > 0) {
      errors.push(`Lengkapi data berikut: ${missing.join(', ')}`)
    }
    if (hasValidationErrors) {
      errors.push('Ada kemasan dengan qty yang melebihi stok')
    }
    return errors
  }

  const isAllFieldsComplete = (packagingRequest: IPackagingRequest) => {
    return getValidationErrors().length === 0
  }

  const savePackagingRequest = () => {
    setIsLoading(true)

    API.post('products', `/packaging/packagingRequest`, {
      body: {
        ...packagingRequest,
        batchNumber: selectedBatch,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat request kemasan.')
        setIsSuccessSnackbarOpen(true)
        // Reset form state
        setPackagingRequest(new PackagingRequest())
        setSelectedBatch(null)
        // Remount finder to clear its internal selection state
        setPackagingFinderKey((k) => k + 1)
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage('Gagal membuat request kemasan.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handlePackagingRequestFieldChange = (value: any, key: string): void => {
    setPackagingRequest((packagingRequestData) => ({
      ...packagingRequestData,
      [key]: value,
    }))
  }

  const onSelectedPackagingChange = (
    packagings: Partial<IPackagingOrder>[]
  ) => {
    handlePackagingRequestFieldChange(packagings, 'packagings')
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Request Kemasan Form' })
  }, [dispatch])

  return (
    <React.Fragment>
      <Paper>
        <React.Fragment>
          <List>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      In/Out
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={packagingRequest.inOutDirection}
                  onChange={(e) =>
                    handlePackagingRequestFieldChange(
                      e.target.value,
                      'inOutDirection'
                    )
                  }
                >
                  <FormControlLabel
                    value={VALUE_IN}
                    control={<Radio color="primary" />}
                    label={'Masuk ke Gudang Kemasan'}
                  />
                  <FormControlLabel
                    value={VALUE_OUT}
                    control={<Radio color="primary" />}
                    label={'Keluar dari Gudang Kemasan'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      Dari/Tujuan
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={packagingRequest.sourceOrDestination}
                  onChange={(e) =>
                    handlePackagingRequestFieldChange(
                      e.target.value,
                      'sourceOrDestination'
                    )
                  }
                >
                  <FormControlLabel
                    value={PackagingRequestSourceOrDestination.rnd}
                    control={<Radio color="primary" />}
                    label={'RnD'}
                  />
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.adjustment}
                      control={<Radio color="primary" />}
                      label={'Adjustment: Barang rusak'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={
                        PackagingRequestSourceOrDestination.productionReturnPackaging
                      }
                      control={<Radio color="primary" />}
                      label={'Produksi Kembalikan Kemasan'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.return}
                      control={<Radio color="primary" />}
                      label={
                        'Customer return (wajib tulis nomor invoice di notes)'
                      }
                    />
                  )}
                  {/* <FormControlLabel
                    value={PackagingRequestSourceOrDestination.return}
                    control={<Radio color="primary" />}
                    label={
                      'Customer Return (manual) wajib tulis nomor invoice di notes'
                    }
                  /> */}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={
                        PackagingRequestSourceOrDestination.fillingAdditional
                      }
                      control={<Radio color="primary" />}
                      label={
                        'Produksi - Request Tambahan (wajib tulis nomor STJ request di notes)'
                      }
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.batchRework}
                      control={<Radio color="primary" />}
                      label={'Rework Bets'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.inNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang masuk tanpa biaya (wajib tulis notes dari siapa)'
                      }
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.outNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang di keluarkan tanpa biaya (wajib tulis notes untuk siapa)'
                      }
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.stjTukarKemasan}
                      control={<Radio color="primary" />}
                      label={'stj tukar kemasan'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.stjTukarKemasan}
                      control={<Radio color="primary" />}
                      label={
                        'stj tukar kemasan (wajib tulis nomor kartu stok kemasan yang sudah di masuk utk tukar)'
                      }
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.pakMul}
                      control={<Radio color="primary" />}
                      label={'pak mul'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.purchaseReturn}
                      control={<Radio color="primary" />}
                      label={'Purchase Return'}
                    />
                  )}
                  {packagingRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={PackagingRequestSourceOrDestination.packing}
                      control={<Radio color="primary" />}
                      label={'Packing'}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </ListItem>

            <PackagingFinder
              key={packagingFinderKey}
              showQty
              selectedPackagings={packagingRequest.packagings}
              getSelectedPackagings={onSelectedPackagingChange}
              editableQty
              showInventory
              showSelectedPackagings
              validateStock={packagingRequest.inOutDirection === VALUE_OUT}
              onValidationChange={(isInvalid: boolean) => {
                // Only treat validation errors (qty > inventory) when this is an OUT request.
                if (packagingRequest.inOutDirection === VALUE_OUT) {
                  setHasValidationErrors(isInvalid)
                } else {
                  // For IN requests we ignore validation errors and clear any existing flag
                  setHasValidationErrors(false)
                }
              }}
            />
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={packagingRequest.notes}
                fullWidth
                label={'Notes Peminta'}
                error={getMissingFields(packagingRequest).includes('Notes')}
                helperText={
                  getMissingFields(packagingRequest).includes('Notes')
                    ? 'Wajib diisi untuk opsi tertentu'
                    : ''
                }
                onChange={(e) =>
                  handlePackagingRequestFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Request Dibuat Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          {/* Missing fields helper */}
          {getMissingFields(packagingRequest).length > 0 && (
            <Box mt={2}>
              <MuiAlert severity="warning" elevation={0} variant="outlined">
                Lengkapi data berikut sebelum menyimpan:{' '}
                {getMissingFields(packagingRequest).join(', ')}
              </MuiAlert>
            </Box>
          )}

          {/* Validation errors */}
          {hasValidationErrors && (
            <Box mt={2}>
              <MuiAlert severity="error" elevation={0} variant="outlined">
                Ada kemasan dengan qty yang melebihi stok. Mohon perbaiki
                sebelum menyimpan.
              </MuiAlert>
            </Box>
          )}

          <Box p={2} mt={2} display="flex" flexDirection="column" alignItems="flex-end">
            <Button
              variant="contained"
              color="primary"
              disabled={
                !isAllFieldsComplete(packagingRequest) ||
                isLoading
              }
              onClick={savePackagingRequest}
            >
              Save
            </Button>
            {!isAllFieldsComplete(packagingRequest) && (
              <Box mt={1}>
                {getValidationErrors().map((err) => (
                  <Typography key={err} variant="caption" color="error" display="block" align="right">
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </React.Fragment>{' '}
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
